import React, { useRef, useState } from 'react';
import AppShell from '../components/AppShell';
import Unread from '../components/Unread';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import { Menu, MenuItem } from '../components/Menu';
import Breadcrumb, { FileGlyph, FolderGlyph } from '../components/resources/Breadcrumb';
import NameDialog from '../components/resources/NameDialog';
import MoveDialog from '../components/resources/MoveDialog';
import { useFiles } from '../data/useFiles';
import { useDateFormat, useDatePattern, useLocale } from '../context/LocaleContext';
import useResourceBrowser from '../hooks/useResourceBrowser';
import useResourceActions from '../hooks/useResourceActions';
import useUploads, { uploadedSentence } from '../hooks/useUploads';
import UploadProgressList from '../components/UploadProgress';
import { useToast } from '../components/Toast';
import { fileLinkMessage } from '../domain/fileLinks';
import { FAQ_URL } from '../lib/consent';
import {
  folderNameRefusal, folderOptions, resourceLimits, resourceMessage, resourceNameRefusal,
} from '../domain/resources';
import { ACCEPTED_FILE_TYPES, fileSizeText } from '../lib/fileLimits';
import s from '../components/resources/resources.module.css';

// Online Resources (#424): the studio's library, as a folder tree under Home.
//
// MMS's page of the same name is the model, in MMS's list grammar — a primary
// action with a menu, a table, a row menu — over the core's tree
// (core/src/resources.rs). Only MMS's lesson drawer half is captured; #424
// holds what the page's capture would settle.
//
// Uploading needs a connection (Storage has no offline queue; see
// data/useFiles.jsx). Everything else here — a folder, a rename, a move — is a
// queued write and works offline like any other.

export default function ResourcesPage() {
  const lib = useFiles();
  const browser = useResourceBrowser({ folders: lib.folders, files: lib.files });
  const actions = useResourceActions(lib.linkFor);
  const formatDate = useDateFormat();
  // A size in the studio's Region: `1,5 MB` in Germany (#549).
  const { shownDecimal } = useLocale();
  const datePattern = useDatePattern();
  const limits = resourceLimits();

  const [addOpen, setAddOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  const [naming, setNaming] = useState(null);
  const [moving, setMoving] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [dropping, setDropping] = useState(false);
  const inputRef = useRef(null);
  const toast = useToast();

  // One at a time, into the folder on screen when each was picked, each with
  // its own progress and its own refusal (#577, hooks/useUploads.js). A
  // refusal stays on its row with Try again and the rest carry on: one wrong
  // type in a drop of six should not cost the other five. What landed is said
  // once, in a toast, when the last file is done.
  const uploads = useUploads(lib.uploadFile, { onUploaded: (rows) => toast(uploadedSentence(rows)) });
  const upload = (list) => uploads.add(list, { folderId: browser.folderId });

  const closeMenu = () => setRowMenu(null);
  const run = (work) => work.then(null, (err) => setError(err?.message || 'That could not be done.'));

  const nameDialog = naming ? {
    newFolder: {
      title: resourceMessage('new_folder'), label: 'Folder name', initial: '', maxLength: limits.folderNameMax,
      refuse: folderNameRefusal, save: (name) => lib.createFolder(name, browser.folderId),
    },
    renameFolder: {
      title: resourceMessage('rename_folder'), label: 'Folder name', initial: naming.target?.name || '', maxLength: limits.folderNameMax,
      refuse: folderNameRefusal, save: (name) => lib.renameFolder(naming.target.id, name),
    },
    renameFile: {
      title: resourceMessage('rename_file'), label: 'File name', initial: naming.target?.name || '', maxLength: limits.resourceNameMax,
      refuse: resourceNameRefusal, save: (name) => lib.renameFile(naming.target.id, name),
    },
  }[naming.kind] : null;

  // Nothing in this folder — said only once the library has been read (#629):
  // an unread one is not "Upload a file once, then link it from any lesson."
  const empty = !browser.folders.length && !browser.files.length;
  const unread = empty && !lib.loaded;

  return (
    <AppShell title={resourceMessage('library_title')}>
      <div className={s.page}>
        <div className={s.bar}>
          <Menu
            open={addOpen}
            onClose={() => setAddOpen(false)}
            align="left"
            trigger={(
              <Button onClick={() => setAddOpen((v) => !v)}>+ {resourceMessage('add_new_menu')} ▾</Button>
            )}
          >
            <MenuItem icon="upload" onClick={() => { setAddOpen(false); inputRef.current?.click(); }}>
              {resourceMessage('upload')}
            </MenuItem>
            <MenuItem onClick={() => { setAddOpen(false); setNaming({ kind: 'newFolder' }); }}>
              {resourceMessage('new_folder')}
            </MenuItem>
          </Menu>
          <span className={s.spacer} />
        </div>

        <Breadcrumb crumbs={browser.crumbs} onOpen={browser.open} />

        <UploadProgressList items={uploads.items} onRetry={uploads.retry} onCancel={uploads.cancel} onDismiss={uploads.dismiss} />

        {error ? <p className={s.error} role="alert">{error}</p> : null}
        {actions.notice ? (
          <p className={actions.notice.kind === 'error' ? s.error : s.status} role="status">{actions.notice.text}</p>
        ) : null}

        <div
          className={[s.wrap, dropping ? s.wrapDropping : ''].filter(Boolean).join(' ')}
          onDragOver={(e) => { e.preventDefault(); setDropping(true); }}
          onDragLeave={() => setDropping(false)}
          onDrop={(e) => { e.preventDefault(); setDropping(false); upload(e.dataTransfer?.files); }}
        >
          <table className={s.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th className={s.right}>Size</th>
                <th>Uploaded</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {browser.folders.map((f) => (
                <tr key={f.id}>
                  <td>
                    <button type="button" className={s.nameButton} onClick={() => browser.open(f.id)}>
                      <FolderGlyph />
                      {f.name}
                    </button>
                  </td>
                  <td className={s.right}><span className={s.muted}>—</span></td>
                  <td><span className={s.muted}>—</span></td>
                  <td className={s.actionsCell}>
                    <Menu
                      open={rowMenu === f.id}
                      onClose={closeMenu}
                      trigger={(
                        <button type="button" className={s.kebab} aria-label={`Actions for ${f.name}`} onClick={() => setRowMenu(rowMenu === f.id ? null : f.id)}>⋮</button>
                      )}
                    >
                      <MenuItem onClick={() => { closeMenu(); browser.open(f.id); }}>Open</MenuItem>
                      <MenuItem icon="pencil" onClick={() => { closeMenu(); setNaming({ kind: 'renameFolder', target: f }); }}>Rename</MenuItem>
                      <MenuItem onClick={() => { closeMenu(); setMoving({ kind: 'folder', target: f }); }}>{resourceMessage('move_title')}…</MenuItem>
                      <MenuItem icon="trash" onClick={() => { closeMenu(); setConfirm({ kind: 'folder', target: f }); }}>Delete</MenuItem>
                    </Menu>
                  </td>
                </tr>
              ))}
              {browser.files.map((r) => {
                const gone = lib.isRemoved(r);
                return (
                  <tr key={r.id}>
                    <td>
                      {gone ? (
                        <span className={s.removedName}>
                          <FileGlyph />
                          <span>{r.name}<br /><span className={s.muted}>{lib.removedMessage(r, datePattern)}</span></span>
                        </span>
                      ) : (
                        <button type="button" className={s.nameButton} onClick={() => actions.open(r.path)}>
                          <FileGlyph />
                          {r.name}
                        </button>
                      )}
                    </td>
                    <td className={s.right}>{shownDecimal(fileSizeText(r.size))}</td>
                    <td>{r.uploadedOn ? formatDate(r.uploadedOn) : ''}</td>
                    <td className={s.actionsCell}>
                      <Menu
                        open={rowMenu === r.id}
                        onClose={closeMenu}
                        trigger={(
                          <button type="button" className={s.kebab} aria-label={`Actions for ${r.name}`} onClick={() => setRowMenu(rowMenu === r.id ? null : r.id)}>⋮</button>
                        )}
                      >
                        {gone ? null : (
                          <>
                            <MenuItem icon="eye" onClick={() => { closeMenu(); actions.open(r.path); }}>Open</MenuItem>
                            <MenuItem icon="paperclip" onClick={() => { closeMenu(); actions.copy(r.path); }}>{resourceMessage('copy_link')}</MenuItem>
                            <MenuItem icon="pencil" onClick={() => { closeMenu(); setNaming({ kind: 'renameFile', target: r }); }}>Rename</MenuItem>
                            <MenuItem onClick={() => { closeMenu(); setMoving({ kind: 'file', target: r }); }}>{resourceMessage('move_title')}…</MenuItem>
                          </>
                        )}
                        <MenuItem icon="trash" onClick={() => { closeMenu(); setConfirm({ kind: 'file', target: r }); }}>Delete</MenuItem>
                      </Menu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {unread ? <Unread failed={lib.failed} /> : empty ? (
            <div className={s.empty}>
              {browser.folderId == null && browser.libraryEmpty
                ? resourceMessage('library_empty')
                : resourceMessage('folder_empty')}
            </div>
          ) : null}
        </div>

        {/* Every file here opens through a link that works for thirty days
            from when it is made, and anyone handed one is told so (#497,
            #575); Copy link hands over that link rather than the address the
            tab ends up on (#515). The FAQ entry on how links expire (#576)
            is linked from the end of this line. */}
        <p className={s.note}>
          {fileLinkMessage('app_note')}
          {' '}
          <a href={`${FAQ_URL}#file-links`} target="_blank" rel="noreferrer">More about file links.</a>
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        style={{ display: 'none' }}
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          e.target.value = '';
          upload(picked);
        }}
      />

      <NameDialog
        open={Boolean(nameDialog)}
        title={nameDialog?.title || ''}
        label={nameDialog?.label || ''}
        initial={nameDialog?.initial || ''}
        maxLength={nameDialog?.maxLength}
        refuse={nameDialog?.refuse}
        onSave={(name) => nameDialog.save(name)}
        onClose={() => setNaming(null)}
      />

      <MoveDialog
        open={Boolean(moving)}
        options={moving ? folderOptions(lib.folders, moving.kind === 'folder' ? moving.target.id : null) : []}
        current={moving ? (moving.kind === 'folder' ? moving.target.parentId || null : moving.target.folderId || null) : null}
        onMove={(target) => (moving.kind === 'folder'
          ? lib.moveFolder(moving.target.id, target)
          : lib.moveFile(moving.target.id, target))}
        onClose={() => setMoving(null)}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.kind === 'folder' ? 'Delete Folder' : 'Delete File'}
        message={confirm?.kind === 'folder'
          ? resourceMessage('delete_folder_confirm')
          : resourceMessage('delete_file_confirm')}
        confirmLabel="Delete"
        discardLabel="Cancel"
        cancelLabel=""
        onDiscard={() => setConfirm(null)}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          const { kind, target } = confirm;
          setConfirm(null);
          run(kind === 'folder' ? lib.removeFolder(target.id) : lib.removeFile(target));
        }}
      />
    </AppShell>
  );
}
